import React, { useEffect, useState } from 'react';
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateResource } from './CRUD';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 }, // padding responsive
  width: { xs: '90%', sm: 400, md: 500 }, // 🔥 clave
  maxHeight: '90vh', // evita que se salga de pantalla
  overflowY: 'auto', // scroll interno
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

export default function OtrosCobrosList() {
  const [otrosCobros, setOtrosCobros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [editCobro, setEditCobro] = useState({
    id: '',
    concepto: '',
    descripcion: '',
    monto: '',
    stock: '',
    tipo_cobro: 'unico',
    estado: 1,
  });
  const [errores, setErrores] = useState({});

  const fetchOtrosCobros = () => {
    setLoading(true);
    const token = sessionStorage.getItem("access_token");
    fetch(`${process.env.REACT_APP_API_URL}/items`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then(res => res.json())
      .then(data => {
        // solo los activos
        setOtrosCobros(data.filter(c => c.estado === 1));
        setLoading(false);
      })
      .catch(err => {
        console.error("Error al cargar otros cobros:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOtrosCobros();
  }, []);

  const handleOpenModal = (cobro = null) => {
    setErrores({});
    if (cobro) {
      setEditCobro({ ...cobro });
    } else {
      setEditCobro({ id: '', concepto: '', monto: '', estado: 1 });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditCobro({ ...editCobro, [name]: value });
  };

  const handleSave = async () => {
    const nuevosErrores = {};
    if (!editCobro.concepto) nuevosErrores.concepto = "El concepto es obligatorio";
    if (editCobro.stock && (isNaN(editCobro.stock) || Number(editCobro.stock) < 0)) nuevosErrores.stock = "Stock inválido";

    if (Object.keys(nuevosErrores).length > 0) {
      setErrores(nuevosErrores);
      return;
    }

    const action = editCobro.id ? 'update' : 'create';
    const url = action === 'create'
      ? `${process.env.REACT_APP_API_URL}/otros_cobros`
      : `${process.env.REACT_APP_API_URL}/otros_cobros/${editCobro.id}`;
    try {
      await updateResource(url, editCobro, action);
      fetchOtrosCobros();
      handleCloseModal();
    } catch (error) {
      console.error("Fallo al guardar otro cobro:", error);
    }
  };

  const handleDelete = async (id) => {
    const token = sessionStorage.getItem("access_token");
    const confirmacion = window.confirm("¿Eliminar este otro cobro?");
    if (!confirmacion) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/otros_cobros/${id}/eliminar`, {
        method: "PUT",
        headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
        body: JSON.stringify({ estado: 0 }),
      });
      if (!response.ok) throw new Error("Error al eliminar");
      fetchOtrosCobros();
    } catch (error) {
      console.error("Error eliminando:", error);
    }
  };

  const columns = [
    { Header: "ID", accessor: "id" },
    { Header: "Concepto", accessor: "concepto" },
    { Header: "Descripcion", accessor: "descripcion"},
    { Header: "Stock", accessor: "stock"},
    { Header: "Tipo", accessor: "tipo_cobro" },
    {
      Header: "Acciones",
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Editar">
            <MDButton color="info" size="medium" onClick={() => handleOpenModal(row.original)}>
              <EditIcon sx={{ fontSize: 28 }} />
            </MDButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <MDButton color="error" size="medium" onClick={() => handleDelete(row.original.id)}>
              <DeleteIcon sx={{ fontSize: 28 }} />
            </MDButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) return <div>Cargando otros cobros...</div>;

  return (
    <div>
      <Box sx={{ display: 'flex', ml: 2 }}>
        <MDButton color="success" onClick={() => handleOpenModal()}>Agregar Otro Cobro</MDButton>
      </Box>

      <DataTable
        table={{ columns, rows: otrosCobros }}
        entriesPerPage={{ defaultValue: 10 }}
        canSearch
        showTotalEntries
        pagination={{ variant: "gradient", color: "info" }}
      />

      {/* Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={style}>
          <Typography variant="h6">{editCobro.id ? "Editar Otro Cobro" : "Agregar Otro Cobro"}</Typography>
          <TextField
            label="Concepto"
            name="concepto"
            fullWidth
            value={editCobro.concepto}
            onChange={handleChange}
            error={!!errores.concepto}
            helperText={errores.concepto || ""}
          />
          <TextField
            label="Descripción"
            name="descripcion"
            multiline
            rows={3}
            fullWidth
            value={editCobro.descripcion}
            onChange={handleChange}
          />
          
          <TextField
            label="Stock"
            name="stock"
            type="number"
            fullWidth
            value={editCobro.stock}
            onChange={handleChange}
            error={!!errores.stock}
            helperText={errores.stock || ""}
          />
          <TextField
            select
            label="Tipo de Cobro"
            name="tipo_cobro"
            fullWidth
            value={editCobro.tipo_cobro}
            onChange={handleChange}
            SelectProps={{ native: true }}
          >
            <option value=""></option>
            <option value="unico">Pago único</option>
            <option value="mensual">Mensual</option>
          </TextField>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <MDButton color="info" onClick={handleSave}>{editCobro.id ? "Actualizar" : "Guardar"}</MDButton>
            <MDButton color="secondary" onClick={handleCloseModal}>Cerrar</MDButton>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
